import React, { type ReactElement, useMemo } from 'react';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import TableActions from 'atom-components/table-actions-lite';
import TableBadgeList from 'atom-components/table-badge-list';
import TableButtons from 'atom-components/table-buttons-lite';
import ServerControlledTable from 'atom-components/tables/server-controlled-table';
import type { SortingProps } from 'atom-components/tables/types';
import TooltipItem from 'atom-components/tooltip-item';
import ActiveMarkerBadge from 'pages/marking/components/active-marker-badge';
import { markingGroupHref } from 'pages/marking/helpers/presentation.helpers';
import { organisationUsersWithMarkerRoleParams } from 'pages/marking/helpers/request-params.helpers';
import { MarkingPath } from 'pages/marking/routes';
import MarkingGroupPolicy from 'policies/marking-group.policy';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { GroupWithGroupUsers } from 'services/assist-api/entities/group/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { usePageAccess } from 'services/providers/page-access.provider';
import DeleteMarkingGroupButton from './delete-marking-group-button';
import type { MarkingGroupRow } from './types';

type Props = {
  isLoading: boolean;
  markingGroups: GroupWithGroupUsers[];
} & SortingProps;

export default function MarkingGroupsTable({
  isLoading,
  markingGroups,
  initialSorting,
  onSort
}: Props): ReactElement {
  const { authorisableUser } = usePageAccess();

  // Get organisation users who have the marker role:
  const currentOrganisation = useAuthCustomer();
  const markerOrganisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisation?.id),
    { enabled: !!currentOrganisation?.id }
  );

  const columnHelper = createColumnHelper<MarkingGroupRow>();
  const columns: ColumnDef<MarkingGroupRow>[] = useMemo(
    () => [
      columnHelper.accessor('groupId', {
        header: () => 'Group ID',
        size: 100
      }),
      columnHelper.accessor('groupName', {
        header: () => 'Group Name',
        size: 200
      }),
      columnHelper.accessor('description', {
        header: () => 'Description',
        size: 200
      }),
      columnHelper.accessor('year', {
        header: () => 'Year',
        enableSorting: false, // Year will always be the same (per table); no reason to sort it.
        size: 100
      }),
      columnHelper.accessor('markerGroupMembers', {
        header: () => 'Markers',
        // Note: column sorting is disabled for the Markers column as sorting multiple records is
        // non-trivial.
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.markerGroupMembers.length === 0) {
            return <span>No Markers in this Group</span>;
          }
          return (
            <TableBadgeList>
              {row.original.markerGroupMembers.map((marker) => (
                <li key={marker.id}>
                  <TooltipItem
                    tooltip={
                      marker.organisationUser?.user.username === undefined
                        ? 'No Username'
                        : `@${marker.organisationUser?.user.username}`
                    }
                  >
                    <ActiveMarkerBadge
                      organisationUser={marker.organisationUser}
                      displayWarning={false}
                      defaultActiveMarkerToOrgUser
                      activeMarker={undefined}
                      isAiMarked={undefined}
                    />
                  </TooltipItem>
                </li>
              ))}
            </TableBadgeList>
          );
        }
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <TableActions>
            {MarkingGroupPolicy.update(authorisableUser) && (
              <TableActions.Link
                tooltip="Edit Marking Group"
                href={markingGroupHref(row.original.groupId)}
                icon={faUsers}
              />
            )}
            <DeleteMarkingGroupButton
              markingGroupId={row.original.groupId}
              markingGroupName={row.original.groupName}
            />
          </TableActions>
        ),
        size: 90
      })
    ],
    [authorisableUser, columnHelper]
  );

  const data: MarkingGroupRow[] = useMemo(
    () =>
      markingGroups.map((markingGroup) => ({
        groupId: markingGroup.id,
        groupName: markingGroup.name,
        description: markingGroup.description || '',
        year: markingGroup.year || '',
        markerGroupMembers:
          markingGroup.users
            ?.filter((gu) =>
              markerOrganisationUsersQuery.data?.json.map((ou) => ou.id).includes(gu.user.id)
            )
            .map((groupUser) => ({
              id: groupUser.id,
              organisationUser: markerOrganisationUsersQuery.data?.json.find(
                (ou) => ou.id === groupUser.user.id
              )
            })) || []
      })) || [],
    [markerOrganisationUsersQuery.data?.json, markingGroups]
  );

  return (
    <>
      {MarkingGroupPolicy.create(authorisableUser) && (
        <TableButtons>
          <TableButtons.Link href={MarkingPath.NewMarkingGroup} icon={faUsers}>
            New Marking Group
          </TableButtons.Link>
        </TableButtons>
      )}
      <ServerControlledTable
        isLoading={isLoading}
        data={data}
        columns={columns}
        initialSorting={initialSorting}
        onSort={onSort}
        entity="marking group"
      />
    </>
  );
}
